// ============================================================================
// Action Validation - Client-side validation for action requirements
// ============================================================================

import {
  type ActionRequirement,
  type ActionValidationResult,
  getPickaxeTier,
  ItemType,
  type ItemTypeValue,
  type RequirementCheckResult,
  RequirementType,
  type ValidationContext,
  validateRequirements,
} from '@dreamgame/shared';
import { skillName, t } from './i18n';
import { pawnInventory } from './pawnGear';
import { selectedPawns } from './selection';

// ============================================================================
// Pickaxe Detection
// ============================================================================

const PICKAXE_ITEM_TYPES: ReadonlyArray<ItemTypeValue> = [
  ItemType.WOODEN_PICKAXE,
  ItemType.STONE_PICKAXE,
  ItemType.BRONZE_PICKAXE,
  ItemType.IRON_PICKAXE,
  ItemType.STEEL_PICKAXE,
  ItemType.SILVER_PICKAXE,
  ItemType.MITHRIL_PICKAXE,
  ItemType.STARMETAL_PICKAXE,
];

const AXE_ITEM_TYPES: ReadonlyArray<ItemTypeValue> = [
  ItemType.WOODEN_AXE,
  ItemType.STONE_AXE,
  ItemType.BRONZE_AXE,
  ItemType.IRON_AXE,
  ItemType.STEEL_AXE,
  ItemType.MITHRIL_AXE,
  ItemType.STARMETAL_AXE,
];

const FISHING_ROD_ITEM_TYPES: ReadonlyArray<ItemTypeValue> = [
  ItemType.WOODEN_FISHING_ROD,
  ItemType.COPPER_FISHING_ROD,
  ItemType.IRON_FISHING_ROD,
  ItemType.STEEL_FISHING_ROD,
];

/**
 * Find the best pickaxe in a pawn's inventory.
 * Returns the pickaxe with the highest maxHardness.
 */
const findBestPickaxe = ({
  inventory,
}: {
  inventory: ReadonlyArray<{ readonly id: number } | null> | null;
}): { itemType: ItemTypeValue; tier: number } | null => {
  if (!inventory) {
    return null;
  }

  let bestPickaxe: { itemType: ItemTypeValue; tier: number } | null = null;

  for (const item of inventory) {
    if (!item) {
      continue;
    }
    const itemType = item.id as ItemTypeValue;
    if (PICKAXE_ITEM_TYPES.includes(itemType)) {
      const tierConfig = getPickaxeTier({ itemType });
      if (tierConfig) {
        if (!bestPickaxe || tierConfig.maxHardness > bestPickaxe.tier) {
          bestPickaxe = { itemType, tier: tierConfig.maxHardness };
        }
      }
    }
  }

  return bestPickaxe;
};

/**
 * Find the best axe in a pawn's inventory.
 * Returns the axe with the highest tier (by position in array).
 */
const findBestAxe = ({
  inventory,
}: {
  inventory: ReadonlyArray<{ readonly id: number } | null> | null;
}): { itemType: ItemTypeValue; tier: number } | null => {
  if (!inventory) {
    return null;
  }

  let bestAxe: { itemType: ItemTypeValue; tier: number } | null = null;

  for (const item of inventory) {
    if (!item) {
      continue;
    }
    const itemType = item.id as ItemTypeValue;
    const axeIndex = AXE_ITEM_TYPES.indexOf(itemType);
    if (axeIndex >= 0) {
      const tier = axeIndex + 1;
      if (!bestAxe || tier > bestAxe.tier) {
        bestAxe = { itemType, tier };
      }
    }
  }

  return bestAxe;
};

/**
 * Find the best fishing rod in a pawn's inventory.
 * Returns the fishing rod with the highest position in the tier list.
 */
const findBestFishingRod = ({
  inventory,
}: {
  inventory: ReadonlyArray<{ readonly id: number } | null> | null;
}): { itemType: ItemTypeValue } | null => {
  if (!inventory) {
    return null;
  }

  let bestRod: { itemType: ItemTypeValue; index: number } | null = null;

  for (const item of inventory) {
    if (!item) {
      continue;
    }
    const itemType = item.id as ItemTypeValue;
    const rodIndex = FISHING_ROD_ITEM_TYPES.indexOf(itemType);
    if (rodIndex >= 0) {
      if (!bestRod || rodIndex > bestRod.index) {
        bestRod = { itemType, index: rodIndex };
      }
    }
  }

  return bestRod ? { itemType: bestRod.itemType } : null;
};

// ============================================================================
// Validation Context Builder
// ============================================================================

// Create empty/default validation context
const createEmptyContext = ({
  hasPawnSelected,
  selectedPawnCount,
}: {
  hasPawnSelected: boolean;
  selectedPawnCount: number;
}): ValidationContext => ({
  hasPawnSelected,
  selectedPawnCount,
  miningLevel: 0,
  woodcuttingLevel: 0,
  fishingLevel: 0,
  equippedPickaxe: null,
  equippedPickaxeTier: 0,
  equippedAxe: null,
  equippedAxeTier: 0,
  equippedFishingRod: null,
  equippedWeapon: null,
  inventory: [],
});

// Build inventory array from pawn inventory items
const buildInventoryArray = ({
  inventoryItems,
}: {
  inventoryItems:
    | ReadonlyArray<{ readonly id: number; readonly quantity: number } | null>
    | undefined;
}): Array<{ itemType: ItemTypeValue; quantity: number }> => {
  const inventory: Array<{ itemType: ItemTypeValue; quantity: number }> = [];
  if (inventoryItems) {
    for (const item of inventoryItems) {
      if (item) {
        inventory.push({ itemType: item.id as ItemTypeValue, quantity: item.quantity });
      }
    }
  }
  return inventory;
};

/**
 * Build validation context from current client state.
 * Uses the first selected pawn for validation.
 */
export const buildValidationContext = (): ValidationContext => {
  const pawns = selectedPawns.value;

  if (pawns.length === 0) {
    return createEmptyContext({ hasPawnSelected: false, selectedPawnCount: 0 });
  }

  const pawn = pawns[0];
  if (!pawn) {
    return createEmptyContext({ hasPawnSelected: true, selectedPawnCount: pawns.length });
  }

  const miningSkill = pawn.skills.find((s) => s.id === 'mining');
  const woodcuttingSkill = pawn.skills.find((s) => s.id === 'woodcutting');
  const fishingSkill = pawn.skills.find((s) => s.id === 'fishing');
  const inventoryItems = pawnInventory.value.get(pawn.networkId);
  const bestPickaxe = findBestPickaxe({ inventory: inventoryItems ?? null });
  const bestAxe = findBestAxe({ inventory: inventoryItems ?? null });
  const bestFishingRod = findBestFishingRod({ inventory: inventoryItems ?? null });

  return {
    hasPawnSelected: true,
    selectedPawnCount: pawns.length,
    miningLevel: miningSkill?.level ?? 0,
    woodcuttingLevel: woodcuttingSkill?.level ?? 0,
    fishingLevel: fishingSkill?.level ?? 0,
    equippedPickaxe: bestPickaxe?.itemType ?? null,
    equippedPickaxeTier: bestPickaxe?.tier ?? 0,
    equippedAxe: bestAxe?.itemType ?? null,
    equippedAxeTier: bestAxe?.tier ?? 0,
    equippedFishingRod: bestFishingRod?.itemType ?? null,
    equippedWeapon: null,
    inventory: buildInventoryArray({ inventoryItems }),
  };
};

// ============================================================================
// Requirement Message Generation
// ============================================================================

/**
 * Convert a requirement check result to a human-readable message.
 */
export const getRequirementMessage = ({ result }: { result: RequirementCheckResult }): string => {
  const req = result.requirement;

  switch (req.type) {
    case RequirementType.HAS_PAWN_SELECTED:
      return t('requirements.noPawnSelected');

    case RequirementType.EQUIPPED_ITEM:
      switch (req.itemCategory) {
        case 'pickaxe':
          return t('requirements.noPickaxe');
        case 'axe':
          return t('requirements.noAxe');
        case 'fishing_rod':
          return t('requirements.noFishingRod');
        case 'weapon':
          return t('requirements.noWeapon');
        default:
          return t('requirements.requirementsNotMet');
      }

    case RequirementType.SKILL_LEVEL: {
      const skill = skillName({ skillId: req.skillId });
      return t('requirements.skillTooLow', {
        skill,
        required: req.minLevel,
        current: result.currentValue as number,
      });
    }

    case RequirementType.ITEM_TIER:
      switch (req.itemCategory) {
        case 'pickaxe':
          return t('requirements.pickaxeTooWeak', { target: req.targetName });
        case 'axe':
          return t('requirements.axeTooWeak', { target: req.targetName });
        default:
          return t('requirements.requirementsNotMet');
      }

    case RequirementType.HAS_ITEM:
      return t('requirements.missingItem', {
        item: String(req.itemType),
        required: req.quantity,
      });

    default:
      return t('requirements.requirementsNotMet');
  }
};

/**
 * Validate requirements and return human-readable messages for unmet ones.
 */
export const validateActionRequirements = ({
  requirements,
}: {
  requirements: ReadonlyArray<ActionRequirement>;
}): {
  canPerform: boolean;
  disabledReasons: ReadonlyArray<string>;
  validationResult: ActionValidationResult;
} => {
  const context = buildValidationContext();
  const validationResult = validateRequirements({ requirements, context });

  const disabledReasons = validationResult.unmetRequirements.map((result) =>
    getRequirementMessage({ result })
  );

  return {
    canPerform: validationResult.canPerform,
    disabledReasons,
    validationResult,
  };
};
